import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { axiosInstanceNewAPI } from '@/services/api/utils/axiosInstance';
import { apiPath } from '@/redux/apiPath';
import { authorSchema } from '@/models/authorSchema';
import { pageableSchema } from '@/models/pageableSchema';
import { PageOf } from '@/types/models';
import { validateDataUsingZodSchema } from '@/utils/validateDataUsingZodSchema';
import { extractAxiosErrorMessage } from '@/utils/getErrorMessage/getErrorMessage.utils';
import { MAP_AUTHORS_SLICE_NAME } from './mapAuthors.constants';
import {
  AddMapAuthorPayload,
  DeleteMapAuthorPayload,
  GetMapAuthorsPayload,
  MapAuthor,
  UpdateMapAuthorPayload,
} from './mapAuthors.types';

function toRejectValue(error: unknown, fallbackMessage: string): { name: string; message: string } {
  if (axios.isAxiosError(error)) {
    return { name: error.name, message: extractAxiosErrorMessage(error) };
  }
  if (error instanceof Error) {
    return { name: error.name, message: error.message };
  }
  return { name: 'Unknown Error', message: fallbackMessage };
}

export const getMapAuthors = createAsyncThunk(
  `${MAP_AUTHORS_SLICE_NAME}/getMapAuthors`,
  async ({ projectId, mapId }: GetMapAuthorsPayload, { rejectWithValue }) => {
    try {
      const response = await axiosInstanceNewAPI.get<PageOf<MapAuthor>>(
        apiPath.getMapAuthors(projectId, mapId),
      );

      const isDataValid = validateDataUsingZodSchema(response.data, pageableSchema(authorSchema));

      return isDataValid
        ? { mapId, authors: response.data.content }
        : rejectWithValue({ message: 'Failed to fetch map authors' });
    } catch (error: unknown) {
      return rejectWithValue(toRejectValue(error, 'Failed to fetch map authors'));
    }
  },
);

export const addMapAuthor = createAsyncThunk(
  `${MAP_AUTHORS_SLICE_NAME}/addMapAuthor`,
  async ({ projectId, mapId, authorData }: AddMapAuthorPayload, { rejectWithValue }) => {
    try {
      const response = await axiosInstanceNewAPI.post<MapAuthor>(
        apiPath.addMapAuthor(projectId, mapId),
        authorData,
      );

      const isDataValid = validateDataUsingZodSchema(response.data, authorSchema);

      return isDataValid
        ? { mapId, author: response.data }
        : rejectWithValue({ message: 'Failed to add map author' });
    } catch (error: unknown) {
      return rejectWithValue(toRejectValue(error, 'Failed to add map author'));
    }
  },
);

export const updateMapAuthor = createAsyncThunk(
  `${MAP_AUTHORS_SLICE_NAME}/updateMapAuthor`,
  async (
    { projectId, mapId, authorId, authorData }: UpdateMapAuthorPayload,
    { rejectWithValue },
  ) => {
    try {
      const response = await axiosInstanceNewAPI.put<MapAuthor>(
        apiPath.updateMapAuthor(projectId, mapId, authorId),
        authorData,
      );

      const isDataValid = validateDataUsingZodSchema(response.data, authorSchema);

      return isDataValid
        ? { mapId, author: response.data }
        : rejectWithValue({ message: 'Failed to update map author' });
    } catch (error: unknown) {
      return rejectWithValue(toRejectValue(error, 'Failed to update map author'));
    }
  },
);

export const deleteMapAuthor = createAsyncThunk(
  `${MAP_AUTHORS_SLICE_NAME}/deleteMapAuthor`,
  async ({ projectId, mapId, authorId }: DeleteMapAuthorPayload, { rejectWithValue }) => {
    try {
      await axiosInstanceNewAPI.delete(apiPath.deleteMapAuthor(projectId, mapId, authorId));

      return { mapId, authorId };
    } catch (error: unknown) {
      return rejectWithValue(toRejectValue(error, 'Failed to delete map author'));
    }
  },
);
